/**
 * /settings/ticket-groups (v11) — fiel à captura do TiFlux: breadcrumb
 * "Configurações / Grupo de tickets", título + Buscar + botão "+ Grupo de
 * tickets", lista Nome | Status | ações e dialog Nome* + Ativo. Leitura para
 * todo o staff; criação/edição/exclusão só com desks.manage (a API barra, o
 * front esconde os controles).
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type { TicketGroup } from '@erp/shared';
import { api, type ApiError } from '@/lib/api-client';
import { usePermissions } from '@/auth/use-permissions';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge, Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { DeskRowAction } from '@/features/desks/desk-stages-section';
import { ticketGroupsQuery } from '@/features/tickets/tickets-queries';
import {
  matchesText,
  normalizeText,
  SettingsBreadcrumb,
  SettingsSearch,
} from '@/features/settings/settings-ui';

const GROUPS_KEY = ['erp', 'ticket-groups'] as const;

function errorMessage(e: unknown): string {
  const err = e as Partial<ApiError> | null;
  if (err?.error === 'validation_error') return 'Dados inválidos — confira os campos.';
  if (err?.status === 403) return 'Você não tem permissão para gerenciar grupos de tickets.';
  if (err?.status === 0) return 'Sem conexão com o servidor.';
  return err?.error ?? 'Erro inesperado.';
}

export function TicketGroupsPage() {
  const qc = useQueryClient();
  const { can, ready } = usePermissions();
  const groupsQ = useQuery(ticketGroupsQuery());
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState<{ group: TicketGroup | null } | null>(null);

  // Escrita gated por desks.manage (padrão das telas de mesa).
  const canManage = ready && can('desks.manage');

  const remove = useMutation({
    mutationFn: async (id: number) => {
      const res = await api<void>(`/ticket-groups/${id}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: GROUPS_KEY }),
  });

  const q = normalizeText(query.trim());
  const list = [...(groupsQ.data ?? [])]
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
    .filter((g) => (q === '' ? true : matchesText(g.name, q)));

  return (
    <div className="rounded-lg border bg-card p-4 shadow-xs sm:p-5">
      {/* Fiel à captura: "Configurações / Grupo de tickets" (sem seção). */}
      <SettingsBreadcrumb section={null} page="Grupo de tickets" />
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <h1 className="text-xl leading-7 font-semibold">Grupo de tickets</h1>
        <div className="ml-auto flex items-center gap-2">
          <SettingsSearch value={query} onChange={setQuery} label="Buscar grupo de tickets" />
          {canManage ? (
            <Button size="sm" className="h-9" onClick={() => setDialog({ group: null })}>
              <Plus className="size-4" strokeWidth={2.5} />
              Grupo de tickets
            </Button>
          ) : null}
        </div>
      </div>

      <div className="mt-4">
        {groupsQ.isPending ? (
          <div className="space-y-2" aria-busy>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : groupsQ.isError ? (
          <EmptyState
            message={`Erro ao carregar grupos de tickets: ${errorMessage(groupsQ.error)}`}
          />
        ) : list.length === 0 ? (
          <EmptyState
            className="h-52"
            message={
              q !== ''
                ? 'Nenhum grupo de tickets encontrado'
                : 'Nenhum grupo de tickets cadastrado'
            }
          />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-10 px-4 font-semibold">Nome</th>
                  <th className="h-10 w-32 px-4 font-semibold">Status</th>
                  <th className="h-10 w-24 px-4">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((g) => (
                  <tr
                    key={g.id}
                    className="h-11 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                  >
                    <td className="max-w-96 truncate px-4 font-medium">{g.name}</td>
                    <td className="px-4">
                      <Badge variant={g.active ? 'success' : 'default'}>
                        {g.active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td className="px-4">
                      {canManage ? (
                        <div className="flex items-center justify-end gap-0.5">
                          <DeskRowAction
                            label="Editar grupo de tickets"
                            onClick={() => setDialog({ group: g })}
                          >
                            <Pencil className="size-3.5" />
                          </DeskRowAction>
                          <DeskRowAction
                            label="Excluir grupo de tickets"
                            destructive
                            disabled={remove.isPending}
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Excluir o grupo "${g.name}"? Os tickets do grupo ficam sem grupo.`
                                )
                              ) {
                                remove.mutate(g.id);
                              }
                            }}
                          >
                            <Trash2 className="size-3.5" />
                          </DeskRowAction>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {remove.error ? (
        <p className="mt-2 text-sm text-destructive">{errorMessage(remove.error)}</p>
      ) : null}

      {dialog ? (
        <TicketGroupDialog group={dialog.group} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}

function TicketGroupDialog({
  group,
  onClose,
}: {
  group: TicketGroup | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(group?.name ?? '');
  const [active, setActive] = useState(group?.active ?? true);

  const save = useMutation({
    mutationFn: async () => {
      const body = { name: name.trim(), active };
      const res = group
        ? await api<TicketGroup>(`/ticket-groups/${group.id}`, { method: 'PUT', body })
        : await api<TicketGroup>('/ticket-groups', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: GROUPS_KEY });
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    save.mutate();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={group ? 'Editar grupo de tickets' : 'Novo grupo de tickets'}
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="ticket-group-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="ticket-group-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Suporte N2"
            required
            autoFocus
          />
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="ticket-group-active"
            aria-label="Grupo ativo"
            checked={active}
            onCheckedChange={setActive}
          />
          <Label htmlFor="ticket-group-active" className="cursor-pointer">
            Ativo
          </Label>
        </div>
        {save.error ? (
          <p className="text-sm text-destructive">{errorMessage(save.error)}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || !name.trim()}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
